
import Vue from 'vue'
import ProjectMixin from '@/_mixins/project'

import { getRequest, patchRequestV2 } from '@/plugins/api-axios'
import { deserialise, serialise } from 'kitsu-core'

import { PSI } from '@/views/Project/PSI/types/psi'

import AddButton from '@/_components/button/add/add.vue'
import PsiCardSettings from '@/views/Project/PSI/components/settings/psiCard.vue'
import PsiSettingsDialog from '@/views/Project/PSI/dialogs/settings/psi.vue'
import FormRevision from '@/views/Project/Form/components/formRevision.vue'
import CopyFormDialog from '@/views/Project/Form/dialogs/copyFormDialog.vue'

type Project = {
    id: string
}

const PsiSettings = Vue.extend({
    mixins: [ProjectMixin],
    components: { CopyFormDialog, PsiCardSettings, AddButton, FormRevision },

    data() {
        return {
            infiniteId: 0,
            offset: 0,
            limit: 20,
            psis: [] as Array<PSI>,
            psiToRevisions: {},
            revisionDialogOpen: false,
            revision: {} as any,
            psi: null as PSI | null,
            isCopyFormDialogVisible: false,
            showCopyFormHelpText: true,
            psiIds: new Set<string>(),
            requiresValidationForActivation: false,
            project: null as Maybe<Project>,
        }
    },

    watch: {
        currentProjectId: {
            immediate: true,
            handler() {
                this.offset = 0
                this.infiniteId = Date.now()
                this.psis = []
            },
        },
    },

    methods: {
        async loadPSIs($state: any) {
            let { data } = await getRequest(
                this.getURL({
                    path: `psis`,
                    base: this.psiBaseUrl,
                    params: {
                        ...(this.isProject && { 'filter[project]': this.currentProject.id }),
                        'page[limit]': this.limit,
                        'page[offset]': this.offset,
                        sort: '-updatedAt',
                    },
                }),
                this.getRequestHeaders('GET')
            )

            let res = deserialise(data).data
            const ids: string[] = []
            res.forEach((form) => {
                if (!this.psiIds.has(form.id)) {
                    this.psiIds.add(form.id)
                    ids.push(form.id)
                }
            })
            await this.getForms(ids)
            res.forEach((form) => {
                if (ids.includes(form.id)) this.psis.push(form)
            })

            if (res.length == this.limit) {
                $state.loaded()
                this.offset += this.limit
            } else {
                $state.complete()
            }
        },

        async getForms(psiIds: Array<string | undefined>) {
            let { data } = await getRequest(
                this.getURL({
                    path: `forms`,
                    base: this.formBaseUrl,
                    params: {
                        'filter[resource]': psiIds.filter(Boolean).join(','),
                        'page[limit]': 100,
                        include: 'resource,activeRevision,inactiveRevisions,draftRevisions',
                    },
                })
            )
            let formData = deserialise(data).data
            formData.forEach((form) => {
                this.$set(this.psiToRevisions, form.resource.data.id, {
                    id: form.id,
                    updatedAt: form.updatedAt,
                    data: this.flattenRevisions(form),
                })
            })
        },

        async handleNewFormAdded(newForm: any) {
            await this.getForms([newForm.id])

            // insert new form at the top of the list
            this.psis.unshift(newForm)

            this.$nextTick(() => {
                const formEl = document.querySelector(`[data-psi-id='${newForm.id}']`)
                if (formEl) {
                    ;(this.$refs.containerEl as HTMLElement)?.parentElement?.scrollTo({ top: 0 })
                    const highlightClasses = [
                        'tw-outline',
                        'tw-outline-blue-400',
                        'tw-outline-offset-2',
                    ]
                    formEl.classList.add(...highlightClasses)
                    setTimeout(() => formEl.classList.remove(...highlightClasses), 3000)
                }
            })
        },

        async handleRevisionChanged(data) {
            this.requiresValidationForActivation = false

            this.refreshFormRevisions(data.resourceId)

            if (data.autoActivate === true && data.revision && data.resourceId) {
                this.activateRevision(data.resourceId, data.revision)
            }

            this.revisionDialogOpen = Boolean(data.keepOpen)
        },

        async handleRevisionAdded(data) {
            this.requiresValidationForActivation = false
            this.refreshFormRevisions(data.resourceId)
            this.revisionDialogOpen = false
            await this.$nextTick()

            if (data.autoActivate === true && data.revision && data.resourceId) {
                this.activateRevision(data.resourceId, data.revision)
            } else {
                if (data.keepOpen) {
                    this.requiresValidationForActivation = Boolean(data.preActivationValidate)
                    this.viewRevision(this.psi, data.revision)
                }
            }
        },

        async activateRevision(psiId: string, formRevision: any) {
            if (!formRevision?.id || !psiId) {
                return
            }

            const psi = this.psis.find((psi) => psi.id === psiId)

            let form = this.psiToRevisions[psiId]
                ? this.psiToRevisions[psiId]
                : formRevision.form.data

            if (!form) return

            let payload = serialise('form', {
                id: form ? form.id : formRevision.form.data.id,
                activeRevision: {
                    data: {
                        type: 'formRevision',
                        id: formRevision.id,
                    },
                },
            })

            // In case we save and activate a revision we need to use that revision updatedAt information to avoid getting error from BE
            // Because when revision is update so is form's updatedAt,but FE doesn't know it.
            // But by default we are using form's updatedAt
            const updatedAt =
                form.updatedAt > formRevision.updatedAt ? form.updatedAt : formRevision.updatedAt
            try {
                const response = await patchRequestV2(
                    this.getURL({
                        path: `forms/${form.id}`,
                        base: this.formBaseUrl,
                    }),
                    payload.data,
                    {
                        validateStatus: () => true, // manually handle response
                    }
                )

                let hasErrors = response.data.errors?.length > 0

                if (!hasErrors) {
                    this.getForms([psiId])
                    return
                }

                let isRevisionDataInvalidForActivation = response.data.errors.some(
                    (error: any) => error.code === 'revisionDataInvalidForActivation'
                )

                if (!isRevisionDataInvalidForActivation) {
                    throw response
                }

                // if revision data is invalid for activation, show dialog
                this.viewRevision(psi, formRevision)
                this.requiresValidationForActivation = true
            } catch (error: any) {
                if (error.data.errors) {
                    this.$store.commit('setErrorSnackBar', {
                        data: error.data.errors,
                        text: this.$i18n.t('vbit_error.default_header'),
                    })
                }
            }
        },

        async refreshFormRevisions(resourceId) {
            if (resourceId) {
                this.getForms([resourceId])
            }
        },

        flattenRevisions(data: any) {
            let revisions: any[] = []

            if (data.activeRevision.data) {
                revisions.push({ ...data.activeRevision.data, _status: 'active' })
            }

            data.draftRevisions.data
                .sort((a: any, b: any) => b.version - a.version)
                .forEach((revision: any) => {
                    revisions.push({ ...revision, _status: 'draft' })
                })

            data.inactiveRevisions.data.forEach((revision: any) => {
                revisions.push({ ...revision, _status: 'inactive' })
            })

            return revisions
        },

        async createPSI() {
            let psi = {
                type: 'psi',
                name: '',
                isBklrPsi: false,
                isBklrAccessPsi: false,
                requireForAppCheckIn: false,
                useCourses: false,
                customIconClass: null,
                project: {
                    data: {
                        type: 'project',
                        id: this.currentProjectId,
                    },
                },
            }

            let res = await this.openDialog(PsiSettingsDialog, { psi, project: this.project })
            if (res && (res as PSI)) {
                this.offset += 1
                this.psis.unshift(res)
                await this.addRevision(res)
            }
        },

        async addRevision(psi: PSI, revisions: Array<any> = []) {
            if (!psi.id) {
                return
            }

            this.psi = psi
            this.revision = {}
            let activeOrLatestDraft = revisions[0]

            if (activeOrLatestDraft) {
                this.revision['data'] = { ...activeOrLatestDraft.data }
            }

            this.revisionDialogOpen = true
        },

        deletePSI(id: string) {
            let index = this.psis.findIndex((psi) => psi.id == id)
            this.psis.splice(index, 1)
            // reset infinite scrolling
            this.offset = 0
            this.infiniteId = Date.now()
        },

        viewRevision(psi: any, revision: any) {
            this.psi = psi
            this.revision = revision
            this.revisionDialogOpen = true
        },

        async getProject() {
            let { data } = await getRequest(
                this.getURL({
                    path: `projects/${this.currentProjectId}`,
                    base: this.psiBaseUrl,
                })
            )
            this.project = deserialise(data).data
        },
    },

    mounted() {
        this.getProject()
    },
})

export default PsiSettings
